import { useCallback } from "react";
import { useEventStore } from "./useEventStore";
import { eventService } from "../services/api";
import { startWS, stopWS } from "../services/ws";

export const useEventList = () => {
  const {
    items,
    pageNo,
    pageCount,
    total,
    selectedTaskId,
    loading,
    error,
    setItems,
    setPageNo,
    setSelectedTaskId,
    setLoading,
    setError,
  } = useEventStore();

  const fetchEvents = useCallback(
    async (taskId: string, page: number): Promise<void> => {
      if (!taskId) return;

      setLoading(true);
      setError(null);

      try {
        const result = await eventService.getLiveEvents(taskId, page);
        setPageNo(page);
        setItems(result.items, result.pageCount, result.total);

        if (page === 1) {
          startWS(taskId);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load events");
      } finally {
        setLoading(false);
      }
    },
    [setItems, setPageNo, setLoading, setError],
  );

  const selectTask = useCallback(
    (taskId: string) => {
      stopWS();
      setSelectedTaskId(taskId);
      if (taskId) fetchEvents(taskId, 1);
    },
    [setSelectedTaskId, fetchEvents],
  );

  const goToPage = useCallback(
    (page: number) => {
      if (loading) return;
      if (page < 1 || page > pageCount) return;
      fetchEvents(selectedTaskId, page);
    },
    [selectedTaskId, pageCount, loading, fetchEvents],
  );

  const refresh = useCallback(() => {
    stopWS();
    fetchEvents(selectedTaskId, 1);
  }, [selectedTaskId, fetchEvents]);

  return {
    items,
    pageNo,
    pageCount,
    total,
    selectedTaskId,
    loading,
    error,
    selectTask,
    goToPage,
    refresh,
  };
};
